/**
 * ApplicationMembersSection
 *
 * Renders the observation nodes rolled up under an Application via
 * HAS_MEMBER, using the shared Table component the rest of the platform
 * uses for "related entities inside a drawer" (cf. TechnologyAssetsImpacted,
 * AffectedAssets):
 *
 *   - IP Addresses: one row per (member-IP, port) pair, columns
 *     IP / Domain / Port / Service / Last Seen. IPs with no ports still
 *     appear as a single row with em-dashes under Port / Service.
 *   - Web Applications: one row per WebApplication member, columns
 *     URL / Name / Status / Tags / Last Seen.
 *
 * Every row routes through `drawerState.handleOpenAssetDrawer` so the user
 * can click into the standard asset drawer for that observation — the same
 * navigation path a row click in the main /assets table uses.
 *
 * Mounted from AssetOverview only when `asset.class === 'application'`.
 */

import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@/components/chariot-ui';
import { DashedTag } from '@/components/chariot-ui';
import { NameColumn } from '@/components/NameColumn';
import {
  getColumnWidth,
  isColumnSortable,
} from '@/components/table/columns/registry';
import { Table } from '@/components/table/Table';
import { AssetStatusComponent } from '@/sections/asset/components/badges/AssetStatus';
import {
  MemberAssetPort,
  useApplicationMembers,
} from '@/sections/asset/hooks/drawer/useApplicationMembers';
import { useGlobalState } from '@/state/global.state';
import { Asset } from '@/types';

export interface ApplicationMembersSectionProps {
  /** The Application asset (class === 'application'). */
  application: Asset;
}

export function ApplicationMembersSection({
  application,
}: ApplicationMembersSectionProps) {
  const { drawerState } = useGlobalState();
  const {
    memberAssetPorts,
    memberWebApplications,
    assetsStatus,
    webApplicationsStatus,
    assetsError,
    webApplicationsError,
  } = useApplicationMembers(application.key);

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="application-members-section"
    >
      <section className="flex flex-col gap-3">
        <h3 className="text-lg font-bold">IP Addresses</h3>
        <Table<MemberAssetPort>
          name="applicationMemberAssets"
          error={assetsError}
          status={assetsStatus}
          data={memberAssetPorts}
          selection={{ hide: true }}
          onRowClick={({ asset }) => {
            // Drill-down opt-out: rows inside the members section must open
            // the *member's* drawer, not loop back to the parent
            // Application. See rollup-frontend-plan.md item B.
            drawerState.handleOpenAssetDrawer(asset, undefined, undefined, {
              resolveToGroup: false,
            });
          }}
          columns={[
            {
              id: '',
              label: 'IP Address',
              copy: true,
              cell: ({ asset }) => asset.identifier,
              ...getColumnWidth('shared', 'ipAddress'),
              sorting: isColumnSortable('shared', 'ipAddress'),
            },
            {
              id: '',
              label: 'Domain Name',
              copy: true,
              cell: ({ asset }) => <NameColumn name={asset.group || '-'} />,
              ...getColumnWidth('shared', 'domainName'),
              sorting: isColumnSortable('shared', 'domainName'),
            },
            {
              id: '',
              label: 'Port',
              copy: true,
              cell: ({ port }) => (port ? port.port.toString() : '—'),
              ...getColumnWidth('shared', 'port'),
              sorting: isColumnSortable('shared', 'port'),
            },
            {
              id: '',
              label: 'Service',
              copy: true,
              cell: ({ port }) => (
                <DashedTag
                  className="max-w-full justify-normal space-x-0 truncate overflow-hidden text-center [&>span]:w-full"
                  label={port?.service || '-'}
                  size="xs"
                />
              ),
              ...getColumnWidth('shared', 'service'),
              sorting: isColumnSortable('shared', 'service'),
            },
            {
              id: 'visited',
              label: 'Last Seen',
              copy: true,
              cell: 'date',
              ...getColumnWidth('shared', 'visited'),
              sorting: isColumnSortable('shared', 'visited'),
            },
          ]}
          noData={{
            icon: (
              <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
            ),
            title: 'No IP addresses roll up under this Application',
          }}
        />
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-lg font-bold">Web Applications</h3>
        <Table<Asset>
          name="applicationMemberWebApplications"
          error={webApplicationsError}
          status={webApplicationsStatus}
          data={memberWebApplications}
          selection={{ hide: true }}
          onRowClick={asset => {
            // Drill-down opt-out — see IP rows above. Member click must
            // not loop back to the Application drawer.
            drawerState.handleOpenAssetDrawer(asset, undefined, undefined, {
              resolveToGroup: false,
            });
          }}
          columns={[
            {
              id: 'identifier',
              label: 'URL',
              copy: true,
              cell: (asset: Asset) => (
                <NameColumn name={asset.identifier || '-'} />
              ),
              ...getColumnWidth('shared', 'domainName'),
              sorting: isColumnSortable('shared', 'domainName'),
            },
            {
              id: 'name',
              label: 'Name',
              copy: true,
              cell: (asset: Asset) => <NameColumn name={asset.name || '-'} />,
              ...getColumnWidth('shared', 'domainName'),
              sorting: isColumnSortable('shared', 'domainName'),
            },
            {
              id: 'status',
              label: 'Status',
              copy: false,
              cell: (asset: Asset) => <AssetStatusComponent asset={asset} />,
              ...getColumnWidth('shared', 'status'),
              sorting: isColumnSortable('shared', 'status'),
            },
            {
              id: '',
              label: 'Tags',
              copy: false,
              cell: (asset: Asset) => (
                <div className="flex flex-wrap gap-1">
                  {(asset.tags ?? []).map(tag => (
                    <DashedTag key={tag} label={tag} size="xs" />
                  ))}
                  {(!asset.tags || asset.tags.length === 0) && (
                    <span className="text-chariot-text-secondary text-xs">
                      —
                    </span>
                  )}
                </div>
              ),
              ...getColumnWidth('shared', 'tags'),
            },
            {
              id: 'visited',
              label: 'Last Seen',
              copy: true,
              cell: 'date',
              ...getColumnWidth('shared', 'visited'),
              sorting: isColumnSortable('shared', 'visited'),
            },
          ]}
          noData={{
            icon: (
              <SoundRecognitionSearchIcon className="text-chariot-stroke text-[100px]" />
            ),
            title: 'No Web Applications roll up under this Application',
          }}
        />
      </section>
    </div>
  );
}
